import React from 'react';
import { TouristSite, TourQuestionnaire } from '../../../types';
import { cn } from '../../../lib/utils';
import { Calendar, MapPin, Clock } from 'lucide-react';

interface Step9_8Props {
  currentPlanningDay: number;
  proposedCircuit: any;
  handleNext: () => void;
  handleBack: () => void;
  totalDays: number;
}

export const Step9_8: React.FC<Step9_8Props> = ({
  currentPlanningDay,
  proposedCircuit,
  handleNext,
  handleBack,
  totalDays
}) => {
  if (!proposedCircuit || proposedCircuit.error) return null;

  // Extract activities for the current day from the proposed circuit
  // The circuit engine might not explicitly group by day in the 'circuit' array, 
  // but we can infer it from the 'steps' array which has 'pickup' and 'dropoff' markers.
  
  const daySteps: any[] = [];
  let dayCount = 0;
  let inCurrentDay = false;

  proposedCircuit.steps.forEach((step: any) => {
    if (step.type === 'pickup') {
      dayCount++;
      if (dayCount === currentPlanningDay) inCurrentDay = true;
    }
    
    if (inCurrentDay) {
      daySteps.push(step);
    }

    if (step.type === 'dropoff' && dayCount === currentPlanningDay) {
      inCurrentDay = false;
    }
  });

  const dayActivities = daySteps.filter(s => s.type === 'activity').map(s => s.site);

  return (
    <div className="flex flex-col h-full p-0 overflow-hidden bg-zinc-950">
      <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar pb-32">
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-emerald-400 font-bold uppercase tracking-widest text-xs">
            <Calendar className="w-4 h-4" />
            Jour {currentPlanningDay} sur {totalDays}
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">Programme des activités</h2>
          <p className="text-zinc-400 text-sm">
            Voici les activités prévues pour cette journée. Nous allons maintenant personnaliser votre restauration et votre hébergement.
          </p>
        </div>

        <div className="space-y-3">
          {dayActivities.map((site, idx) => (
            <div 
              key={`${site.id}-${idx}`}
              className="flex items-center gap-4 p-4 bg-zinc-900/50 border border-zinc-800 rounded-2xl"
            >
              <div className="w-16 h-16 rounded-xl bg-zinc-800 overflow-hidden shrink-0 border border-white/5">
                {site.image ? (
                  <img src={site.image} alt={site.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xl">📍</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-bold text-sm truncate">{site.name}</h3>
                <div className="flex items-center gap-3 mt-1">
                  <div className="flex items-center gap-1 text-[10px] text-zinc-500">
                    <Clock className="w-3 h-3" />
                    {site.durationMinutes || 60} min
                  </div>
                  <div className="flex items-center gap-1 text-[10px] text-zinc-500">
                    <MapPin className="w-3 h-3" />
                    {site.city || 'Cévennes'}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {dayActivities.length === 0 && (
            <div className="p-8 text-center bg-zinc-900/30 rounded-3xl border border-dashed border-zinc-800">
              <p className="text-zinc-500 text-sm italic">Aucune activité spécifique pour ce jour.</p>
            </div>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-30 bg-zinc-950/90 backdrop-blur-xl border-t border-white/10">
        <div className="max-w-7xl mx-auto p-4 md:p-6 flex gap-3">
          <button 
            onClick={handleBack}
            className="flex-1 py-3 bg-zinc-800 text-white rounded-xl font-semibold text-sm hover:bg-zinc-700 transition-colors"
          >
            Retour
          </button>
          <button 
            onClick={handleNext}
            className="flex-[2] py-3 bg-emerald-500 text-white rounded-xl font-semibold text-sm hover:bg-emerald-600 transition-colors shadow-[0_0_15px_rgba(16,185,129,0.3)]"
          >
            Valider le programme
          </button>
        </div>
      </div>
    </div>
  );
};
